import React, { useEffect, useState, useCallback, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CharacterCard } from "./CharacterCard";
import { QuestionCard } from "./QuestionCard";
import { Scoreboard } from "./Scoreboard";
import { RetroMarquee } from "./Marquee";
import { DebateTimer } from "./Timer";
import { RulesDialog } from "./RulesDialog";
import { fetchRound } from "../lib/api";
import { playClick, playPoint } from "../lib/sound";
import { MuteToggle } from "./MuteToggle";
import { Sparkles, RotateCcw, Loader2 } from "lucide-react";
import { toast } from "sonner";

const TEAM1_COLOR = "#FF2A54";
const TEAM2_COLOR = "#4D4DFF";
const BUFFER_TARGET = 2; // keep 2 fresh rounds preloaded

export const GameBoard = ({ team1Name, team2Name, onWinner, onExit }) => {
  const [round, setRound] = useState(null); // { c1, c2, q, role1 }
  const [score1, setScore1] = useState(0);
  const [score2, setScore2] = useState(0);
  const [loadingFirst, setLoadingFirst] = useState(true);
  const [dealing, setDealing] = useState(false);
  const [roundKey, setRoundKey] = useState(0);

  // Buffer of pre-fetched rounds. Each item: { subjects: [s1, s2], question }
  const bufferRef = useRef([]);
  const inflightRef = useRef(0);
  // Recent items to bias against immediate repeats from AI
  const recentSubjectsRef = useRef(new Set());
  const recentQuestionsRef = useRef(new Set());

  const RECENT_MAX = 30;

  const remember = (set, item) => {
    set.add(item);
    if (set.size > RECENT_MAX) {
      const first = set.values().next().value;
      set.delete(first);
    }
  };

  // Pre-fetch one round into the buffer
  const fetchOne = useCallback(async () => {
    inflightRef.current += 1;
    try {
      const recentS = Array.from(recentSubjectsRef.current).slice(-30);
      const recentQ = Array.from(recentQuestionsRef.current).slice(-30);
      // Try up to 3 times to get a non-recently-seen round
      let r = null;
      for (let attempt = 0; attempt < 3; attempt++) {
        const resp = await fetchRound(recentS, recentQ);
        const [s1, s2] = resp.subjects;
        const q = resp.question;
        const subjectClash =
          recentSubjectsRef.current.has(s1) || recentSubjectsRef.current.has(s2);
        const questionClash = recentQuestionsRef.current.has(q);
        if (!subjectClash && !questionClash) {
          r = resp;
          break;
        }
        if (attempt === 2) r = resp; // accept on last attempt
      }
      if (r) bufferRef.current.push(r);
    } catch (e) {
      // Silent — primary flow handles failure
    } finally {
      inflightRef.current -= 1;
    }
  }, []);

  // Top up buffer to BUFFER_TARGET
  const topUpBuffer = useCallback(() => {
    const need = BUFFER_TARGET - (bufferRef.current.length + inflightRef.current);
    for (let i = 0; i < need; i++) fetchOne();
  }, [fetchOne]);

  // Take next round from buffer (await if empty)
  const consumeRound = useCallback(async () => {
    if (bufferRef.current.length === 0) {
      try {
        const recentS = Array.from(recentSubjectsRef.current).slice(-30);
        const recentQ = Array.from(recentQuestionsRef.current).slice(-30);
        const r = await fetchRound(recentS, recentQ);
        bufferRef.current.push(r);
      } catch (e) {
        toast.error("Couldn't reach the deck. Try again.");
        return null;
      }
    }
    const next = bufferRef.current.shift();
    topUpBuffer();
    return next;
  }, [topUpBuffer]);

  const dealRound = useCallback(async () => {
    setDealing(true);
    const r = await consumeRound();
    if (!r) {
      setDealing(false);
      return;
    }
    const [c1, c2] = r.subjects;
    const q = r.question;
    remember(recentSubjectsRef.current, c1);
    remember(recentSubjectsRef.current, c2);
    remember(recentQuestionsRef.current, q);
    const role1 = Math.random() < 0.5 ? "FOR" : "AGAINST";
    setRound({ c1, c2, q, role1 });
    setRoundKey((k) => k + 1);
    setDealing(false);
  }, [consumeRound]);

  // Initial load: fetch first round + warm buffer
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const first = await fetchRound([], []);
        if (cancelled) return;
        bufferRef.current.push(first);
        await dealRound();
        if (cancelled) return;
        topUpBuffer();
      } catch (e) {
        if (cancelled) return;
        toast.error("Couldn't load the first round. Refresh to retry.");
      } finally {
        if (!cancelled) setLoadingFirst(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addPoint = (team) => {
    playPoint();
    if (team === 1) {
      const next = score1 + 1;
      setScore1(next);
      if (next >= 10) {
        setTimeout(() => onWinner({ name: team1Name, color: TEAM1_COLOR }), 600);
        return;
      }
    } else {
      const next = score2 + 1;
      setScore2(next);
      if (next >= 10) {
        setTimeout(() => onWinner({ name: team2Name, color: TEAM2_COLOR }), 600);
        return;
      }
    }
    setTimeout(() => dealRound(), 350);
  };

  const reshuffle = () => {
    playClick();
    dealRound();
  };

  if (loadingFirst) {
    return (
      <div className="min-h-screen bg-halftone flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="mx-auto animate-spin" size={48} />
          <p className="font-anton uppercase text-3xl mt-4">
            Shuffling the deck...
          </p>
          <p className="font-mono-debate uppercase tracking-widest text-xs mt-2 text-[#111111]/60">
            (almost there)
          </p>
        </div>
      </div>
    );
  }

  if (!round) return null;

  const role2 = round.role1 === "FOR" ? "AGAINST" : "FOR";
  const winThreshold = 10;

  return (
    <div className="min-h-screen bg-halftone flex flex-col" data-testid="game-board">
      <RetroMarquee />

      {/* Top bar */}
      <div className="px-4 md:px-8 py-4 flex items-center justify-between gap-3 border-b-4 border-[#111111] bg-[#FFF4E0]">
        <h1 className="font-anton uppercase text-2xl md:text-4xl">
          MASTER <span className="text-[#FF2A54]">DEBATERS</span>
        </h1>
        <div className="flex items-center gap-2">
          <MuteToggle />
          <RulesDialog />
          <button
            onClick={() => { playClick(); onExit(); }}
            data-testid="exit-game-btn"
            className="font-mono-debate font-bold uppercase text-xs md:text-sm tracking-widest border-2 border-[#111111] px-3 py-2 hover:bg-[#111111] hover:text-[#FFF4E0] transition-colors"
          >
            ✕ Quit
          </button>
        </div>
      </div>

      <div className="flex-1 px-4 md:px-8 py-6 md:py-10">
        <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr_260px] gap-6 md:gap-10 max-w-7xl mx-auto">
          {/* Team 1 Scoreboard */}
          <Scoreboard
            teamName={team1Name}
            score={score1}
            onAddPoint={() => addPoint(1)}
            color={TEAM1_COLOR}
            side="left"
            disabled={score1 >= winThreshold || score2 >= winThreshold || dealing}
            testIdPrefix="team1"
          />

          {/* Center: cards */}
          <div className="flex flex-col items-center gap-6 md:gap-10">
            <AnimatePresence mode="wait">
              <motion.div
                key={`q-${roundKey}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="w-full"
              >
                <QuestionCard question={round.q} />
              </motion.div>
            </AnimatePresence>

            <AnimatePresence mode="wait">
              <motion.div
                key={`cards-${roundKey}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="grid grid-cols-1 sm:grid-cols-2 gap-6 md:gap-10 place-items-center w-full"
              >
                <CharacterCard
                  name={round.c1}
                  role={round.role1}
                  index={0}
                  teamLabel={team1Name}
                />
                <CharacterCard
                  name={round.c2}
                  role={role2}
                  index={1}
                  teamLabel={team2Name}
                />
              </motion.div>
            </AnimatePresence>

            {/* Debate rule reminder */}
            <div
              data-testid="debate-rule-banner"
              className="bg-[#111111] text-[#FFD700] border-4 border-[#111111] shadow-brutal-sm px-5 py-3 max-w-2xl text-center"
            >
              <p className="font-anton uppercase text-base md:text-lg leading-tight">
                ★ Each player MUST use their word or phrase in their debate ★
              </p>
              <p className="font-mono-debate font-bold uppercase tracking-widest text-[10px] mt-1 text-[#FFF4E0]/70">
                Tap "Rules" up top for the full rulebook
              </p>
            </div>

            {/* Timers */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full max-w-2xl">
              <DebateTimer
                durationSeconds={180}
                label="Debate · 3:00"
                hint="3 minutes for arguments"
                accentColor="#FFD700"
                resetSignal={roundKey}
                testIdPrefix="debate-timer"
              />
              <DebateTimer
                durationSeconds={60}
                label="Final / Rebuttal · 1:00"
                hint="1 minute for closers"
                accentColor="#39FF14"
                resetSignal={roundKey}
                testIdPrefix="final-timer"
              />
            </div>

            {/* Action button */}
            <div className="flex flex-wrap justify-center gap-3 mt-2">
              <button
                onClick={reshuffle}
                disabled={dealing}
                data-testid="reshuffle-btn"
                className="btn-brutal-press bg-[#4D4DFF] text-white border-4 border-[#111111] shadow-brutal-sm font-anton uppercase text-lg md:text-xl px-5 py-3 flex items-center gap-2 disabled:opacity-60"
              >
                {dealing ? (
                  <Loader2 size={20} className="animate-spin" />
                ) : (
                  <Sparkles size={20} />
                )}
                {dealing ? "Shuffling..." : "New Round"}
              </button>
            </div>

            <div className="font-mono-debate uppercase tracking-widest text-[10px] text-[#111111]/60 text-center">
              <RotateCcw size={10} className="inline mr-1" />
              Massive curated deck · No two rounds the same
            </div>
          </div>

          {/* Team 2 Scoreboard */}
          <Scoreboard
            teamName={team2Name}
            score={score2}
            onAddPoint={() => addPoint(2)}
            color={TEAM2_COLOR}
            side="right"
            disabled={score1 >= winThreshold || score2 >= winThreshold || dealing}
            testIdPrefix="team2"
          />
        </div>
      </div>
    </div>
  );
};
